<script lang="ts">
import type { ResolvedMonospaceCollection, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { CardsDisplayOptions } from './LayoutCards.vue';
import { CollectionFieldSelect, DisplayTemplateInput, fieldIdToDisplayTemplateAst, getCollectionDefaultDisplayTemplate, isToOneFileRelation } from '#layers/data-model/public';
import { useInitializedDisplayOptionsModel } from '#layers/listing/composables/use-initialized-display-options-model';
import { initialSortState } from '#layers/listing/utils/initial-sort-state';

function guessCollectionThumbnailField(collection: ResolvedMonospaceCollection) {
	return collection.relationFields.find((field) => isToOneFileRelation(field));
}

export function initializeLayoutCardsOptions({ collection, options }: { collection: ResolvedMonospaceCollection; options?: Partial<CardsDisplayOptions> | null }): CardsDisplayOptions {
	return {
		sorting: initialSortState(collection),
		...options,
		titleTemplate: options?.titleTemplate
			?? (options?.titleField ? fieldIdToDisplayTemplateAst(options.titleField) : null)
			?? getCollectionDefaultDisplayTemplate(collection)
			?? null,
		descriptionTemplate: options?.descriptionTemplate
			?? (options?.descriptionField ? fieldIdToDisplayTemplateAst(options.descriptionField) : null)
			?? null,
		thumbnailField: options?.thumbnailField ?? guessCollectionThumbnailField(collection)?.id ?? null,
		cardSize: options?.cardSize ?? 'md',
	};
}
</script>

<script setup lang="ts">
import { computed } from 'vue';
import LayoutDisplayOptionSort from '#layers/listing/components/Layout/DisplayOption/LayoutDisplayOptionSort.vue';

interface CardsDisplayOptionProps {
	collection: ResolvedMonospaceCollection;
	displayOptions?: CardsDisplayOptions;
	sharedDisplayOptions?: CardsDisplayOptions;
}

const props = defineProps<CardsDisplayOptionProps>();

const emit = defineEmits<{
	'update:displayOptions': [value: CardsDisplayOptions | undefined];
}>();

const model = useInitializedDisplayOptionsModel(
	{
		baselineDisplayOptions: () => props.sharedDisplayOptions,
		displayOptions: () => props.displayOptions,
		initializedBaselineOptions: () => initializeLayoutCardsOptions({ collection: props.collection, options: props.sharedDisplayOptions }),
		initializedOptions: () => initializeLayoutCardsOptions({ collection: props.collection, options: props.displayOptions }),
	},
	(value) => emit('update:displayOptions', value),
);

const collectionDisplayTemplate = computed(() => getCollectionDefaultDisplayTemplate(props.collection));

function isThumbnailField(field: ResolvedMonospaceSingleRelationField) {
	return isToOneFileRelation(field);
}
</script>

<template>
	<template v-if="model">
		<LayoutDisplayOptionSort v-model="model.sorting" :collection />
		<DFormSeparator class="-mx-4 my-1" />
		<DFormField :label="$t('common.title')">
			<DisplayTemplateInput
				v-model="model.titleTemplate"
				:default-value="collectionDisplayTemplate"
				:collection
			/>
		</DFormField>
		<DFormField :label="$t('common.description')">
			<DisplayTemplateInput
				v-model="model.descriptionTemplate"
				:collection
			/>
		</DFormField>
		<DFormField :label="$t('common.thumbnail')">
			<CollectionFieldSelect
				v-model="model.thumbnailField"
				:include-primitive-fields="false"
				:collection
				:filter="isThumbnailField"
			/>
		</DFormField>
		<DFormField :label="$t('layout.cards.card_size.label')">
			<DSelect
				v-model="model.cardSize"
				:items="[
					{ label: $t('layout.cards.card_size.options.small'), value: 'sm' },
					{ label: $t('layout.cards.card_size.options.medium'), value: 'md' },
					{ label: $t('layout.cards.card_size.options.large'), value: 'lg' },
				]"
			/>
		</DFormField>
	</template>
</template>
